/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Alert, Button, INodeProps, Waiting } from '@msdyn365-commerce-modules/utilities';
import { IWishlistActionSuccessResult, PromoCodeComponent } from '@msdyn365-commerce/components';
import MsDyn365, { getUrlSync, IImageSettings, RichTextComponent } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { CartLine } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { when, computed } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { IFarmlandsCartData } from './farmlands-cart.data';
import { IFarmlandsCartProps } from './farmlands-cart.props.autogenerated';
import { CartLineItems, ICartlinesViewProps } from './components/farmlands-cart-line-items';
import { DeliveryMethods } from './components/farmlands-cart-delivery-methods';
import { hasCartLineValidationErrors, retrieveDeliveryOptionsForCartItems } from '../../helpers/cart-validation-helpers';
import { IOrderSummaryLines, OrderSummary } from '../../common/components/order-summary-lineitem';
import {
    hasEcommerceAccess,
    isShareholder,
    isShareholderAccountDisabled,
    isShareholderAccountNotInactive,
    isShareholderAccountNotOnHold
} from '../../common/method/functions';
import { IDeliveryOptions } from '../../actions/farmlands-get-delivery-options-calls.action';
import { getContactDetailsAsync } from '../../actions/farmlands-get-contact-details-calls.action';
import { inventoryStocks } from './inventoryStore';

export interface ICartViewProps extends IFarmlandsCartProps<IFarmlandsCartData> {
    className: string;
    deliveryText: string;
    clickCollectText: string;
    title?: React.ReactNode;
    CartlinesWrapper: INodeProps;
    cartlines?: ICartlinesViewProps[];
    cart: ICartState | undefined;
    deliveryMethodHeading: React.ReactNode;
    deliveryMethodOptions: React.ReactNode;
    orderSummaryHeading: React.ReactNode;
    orderSummaryAccountDetails: React.ReactNode;
    orderSummaryLineitems?: IOrderSummaryLines;
    OrderSummaryWrapper: INodeProps;
    OrderSummaryItems: INodeProps;
    OrderSummaryErrors?: IOrderSummaryErrors;
    checkoutWithAccountButton: React.ReactNode;
    checkoutWithCreditCardButton: React.ReactNode;
    checkoutAsSignInUserButton: React.ReactNode;
    checkoutAsGuestButton: React.ReactNode;
    isCartEmpty: boolean;
    promotionBlocksWrapper: INodeProps;
    promotionBlocks?: React.ReactNode;
    storeSelector?: React.ReactNode;
    backToShoppingButton: React.ReactNode;
    continuingShoppingButton: React.ReactNode;
    waitingComponent: React.ReactNode;
    promoCode: React.ReactNode;
    cartLoadingStatus: string;
    cartDataResult: boolean;
    loadingSpinner: React.ReactNode;
    isUserAuthorised: boolean;
    hasEcomPermission: boolean;
    hasCheckedUserAuthority: boolean;
    removeItemClickHandler(cartlineToRemove: CartLine): void;
    moveToWishlistSuccessHandler(result: IWishlistActionSuccessResult, cartlineId: CartLine): void;
}

export interface IOrderSummaryErrors {
    Wrapper: INodeProps;

    header: React.ReactNode;
    errors: React.ReactNode[];
}

interface ICartStateLocal {
    showSpinner: boolean;
    isShareholderUser: boolean;
    isAccountNotInactive: boolean;
    isAccountNotOnHold: boolean;
    isUserAuthorised: boolean;
    hasCheckedUserAuthority: boolean;
    hasEcomPermission: boolean;
    deliveryOptions: IDeliveryOptions[];
    isSingleAccount: boolean;
}

/**
 *
 * Cart component
 * @extends {React.PureComponent<IFarmlandsCartProps<IFarmlandsCartData>>}
 */
@observer
class Cart extends React.Component<IFarmlandsCartProps<IFarmlandsCartData>, ICartStateLocal> {
    @computed public get displayName(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        return customer ? customer.FirstName || customer.Name : undefined;
    }

    @computed public get accountNumber(): Readonly<string | undefined> {
        return this.props.data.accountInformation.result?.AccountNumber;
    }

    constructor(props: IFarmlandsCartProps<IFarmlandsCartData>) {
        super(props);
        this.state = {
            showSpinner: false,
            isShareholderUser: false,
            isAccountNotInactive: false,
            isAccountNotOnHold: false,
            isUserAuthorised: false,
            hasCheckedUserAuthority: false,
            hasEcomPermission: true,
            deliveryOptions: [],
            isSingleAccount: false,
        };
    }

    public async componentDidMount(): Promise<void> {
        const {
            data: {
                cart: { result: cart },
                products: { result: products }
            }
        } = this.props;

        when(
            () => cart!.status === 'READY',
            async () => {
                // check if the logged-in user has ecomm purchase rights
                if (this.props.context.request.user.token) {
                    const userContactDetails = await getContactDetailsAsync(
                        { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                        JSON.parse(atob(this.props.context.request.user.token.split('.')[1])).oid
                    );
                    if (!hasEcommerceAccess(userContactDetails)) {
                        this.setState({
                            hasEcomPermission: false
                        });
                    }
                    this.setState({
                        isSingleAccount: userContactDetails?.CustomerAccounts.length <= 1
                    });
                }

                this.setState({
                    isShareholderUser: isShareholder(cart?.cart!) || false,
                    isAccountNotInactive: isShareholderAccountNotInactive(cart?.cart.ExtensionProperties!),
                    isAccountNotOnHold: isShareholderAccountNotOnHold(cart?.cart.ExtensionProperties!),
                    isUserAuthorised: !isShareholderAccountDisabled(cart?.cart.ExtensionProperties!),
                    hasCheckedUserAuthority: true
                });
            }
        );

        when(
            () => cart === undefined,
            () => {
                this.setState({
                    hasCheckedUserAuthority: true
                });
            }
        );

        await retrieveDeliveryOptionsForCartItems(this.props.context.actionContext, products!).then((response) => {
            this.setState({
                deliveryOptions: response
            });
        });
        if (cart?.cart && cart?.cart.CartLines && cart?.cart.CartLines.length > 0) {
            cart?.cart.CartLines.forEach(product => {
                let productObj = {
                    productInfo: {
                        productName: product.Description,
                        productId: product.ProductId,
                        sku: product.ItemId
                    }
                };
                (Window as any).digitalData.product.push(productObj);
            })
        }
        window.dispatchEvent(new CustomEvent('CartView'));
    }

    public render(): JSX.Element | null {
        const {
            data: {
                cart: { result: cart }
            },
            config: {
                deliveryText,
                clickCollectText,
                onHoldMessage,
                suspendedMessage,
                ecomPermissionMessage
            },
            context: {
                request: {
                    user: { isAuthenticated, signInUrl }
                }
            },
            resources
        } = this.props;

        const { isShareholderUser, isAccountNotInactive, isAccountNotOnHold, hasEcomPermission } = this.state;

        const signInURL = this._getSignInURL(signInUrl || '');
        const checkoutUrl = getUrlSync('checkout', this.props.context.actionContext) || '';
        const checkoutAsShareholderURL = getUrlSync('checkout-shareholder-only', this.props.context.actionContext) || '';
        const backToShoppingUrl = getUrlSync('home', this.props.context.actionContext) || '';
        const isCartEmpty = cart && cart.cart.CartLines && cart.cart.CartLines.length === 0;
        const cartLines = this._getCartLines();

        const hasErrors = cartLines ? cartLines.some((cartline) => cartline.error !== undefined) : false;
        const backorderError = cart ? this._checkBackorderErrors(cart.cart.CartLines) : false;

        const hasValidationErrors =
            cartLines && this.state.deliveryOptions.length > 0
                ? hasCartLineValidationErrors(cart?.cart.CartLines || [], this.state.deliveryOptions)
                : false;

        const viewProps = {
            ...this.props,
            className: classnames('ms-cart'),
            title: <h1 className={'msc-cart__heading'}>{resources.cartTitle}</h1>,
            CartlinesWrapper: { className: 'msc-cartline-wrapper' },
            promotionBlocksWrapper: { className: 'msc-promotion-wrapper' },
            OrderSummaryWrapper: { className: 'msc-order-summary-wrapper' },
            OrderSummaryItems: { className: 'msc-order-summary__items' },
            cartlines: cartLines,
            promoCode: this._getPromoCode(),
            cart: this.props.data.cart.result,
            context: this.props.context,
            typeName: this.props.typeName,
            telemetry: this.props.telemetry,
            id: this.props.id,
            removePromoAriaLabelFormat: resources.removePromoAriaLabelFormat,
            deliveryMethodHeading: <h2 className={'msc-order-summary__heading'}>{resources.deliveryMethodTitle}</h2>,
            deliveryMethodOptions: this._getDeliveryMethods(),
            orderSummaryHeading:
                this.props.data.products &&
                    this.props.data.products.result &&
                    cart &&
                    cart.cart.CartLines &&
                    cart.cart.CartLines.length > 0 ? (
                    <h2 className={'msc-order-summary__heading'}>{resources.orderSummaryTitle}</h2>
                ) : null,
            orderSummaryAccountDetails: this._getOrderSummaryAccountDetails(),
            orderSummaryLineitems: this._getOrderSummaryLineItems(),
            checkoutWithAccountButton: !isCartEmpty && isAuthenticated && isShareholderUser && (
                <>
                    <Button
                        disabled={hasErrors || backorderError || hasValidationErrors || !(isAccountNotOnHold && isAccountNotInactive) || !hasEcomPermission}
                        className={'msc-cart__btn-checkout'}
                        href={checkoutAsShareholderURL}
                        title={resources.checkoutWithAccountButtonTitle}
                    >
                        {resources.checkoutWithAccountButtonTitle}
                    </Button>
                    {!isAccountNotOnHold && <RichTextComponent className='shareholder-warning' text={onHoldMessage} />}
                    {!isAccountNotInactive && <RichTextComponent className='shareholder-warning' text={suspendedMessage} />}
                    {!hasEcomPermission && <RichTextComponent className='shareholder-warning' text={ecomPermissionMessage} />}
                </>
            ),
            checkoutWithCreditCardButton: !isCartEmpty && isAuthenticated && (
                <Button
                    disabled={hasErrors || backorderError || hasValidationErrors || !hasEcomPermission}
                    className={'msc-cart__btn-checkout'}
                    href={checkoutUrl}
                    title={resources.checkoutWithCardButtonTitle}
                >
                    {resources.checkoutWithCardButtonTitle}
                </Button>
            ),
            checkoutAsSignInUserButton: !isCartEmpty && !isAuthenticated && (
                <Button
                    disabled={hasErrors || backorderError || hasValidationErrors || !hasEcomPermission}
                    className={'msc-cart__btn-checkout'}
                    href={signInURL}
                    title={resources.checkoutButtonTitle}
                >
                    {resources.checkoutButtonTitle}
                </Button>
            ),
            checkoutAsGuestButton: !isCartEmpty && !isAuthenticated && (
                <Button
                    disabled={hasErrors || backorderError || hasValidationErrors || !hasEcomPermission}
                    className={'msc-cart__btn-guestcheckout'}
                    href={checkoutUrl}
                    title={resources.guestCheckoutButtonTitle}
                >
                    {resources.guestCheckoutButtonTitle}
                </Button>
            ),
            backToShoppingButton: (
                <Button className={'msc-cart__btn-backtoshopping'} href={backToShoppingUrl} title={resources.backToShoppingButtonTitle}>
                    {resources.backToShoppingButtonTitle}
                </Button>
            ),
            continuingShoppingButton: (
                <Button className={'msc-cart__btn-continue-shopping'} href={backToShoppingUrl} title={resources.backToShoppingButtonTitle}>
                    <i className='msc-cart__btn-continue-shopping-icon material-icons'>arrow_left</i>
                    {resources.backToShoppingButtonTitle}
                </Button>
            ),
            promotionBlocks:
                this.props.slots.promotionBlocks && this.props.slots.promotionBlocks.length > 0 ? this.props.slots.promotionBlocks : null,
            storeSelector:
                this.props.slots.storeSelector && this.props.slots.storeSelector.length > 0 ? this.props.slots.storeSelector[0] : null,
            removeItemClickHandler: this._removeItemFromCartHandler,
            moveToWishlistSuccessHandler: this._moveToWishlistSuccessHandler,
            waitingComponent: <Waiting className='msc-waiting-circular-cart msc-waiting-circular msc-waiting-lg' />,
            cartLoadingStatus: this.props.data.cart.status === 'FAILED' ? this.props.resources.cartLoadingErrorMessage : null,
            cartDataResult: this.props.data.cart.result && this.props.data.cart.result.isEmpty,
            loadingSpinner: this.state.showSpinner && (
                <div className='msc-cart__loading-spinner-mask'>
                    <div className='msc-cart__loading-spinner-wrapper'>
                        <div className='msc-cart__loading-spinner msc-waiting-circular msc-waiting-lg' />
                    </div>
                </div>
            ),
            isUserAuthorised: this.state.isUserAuthorised,
            hasEcomPermission: this.state.hasEcomPermission,
            hasCheckedUserAuthority: this.state.hasCheckedUserAuthority,
            OrderSummaryErrors: this._getOrderSummaryErrors(cartLines),
            deliveryText,
            clickCollectText,
            isCartEmpty
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private setShowSpinner = (show: boolean) => {
        this.setState({ showSpinner: show });
    };

    private _getDeliveryMethods = () => {
        const {
            data: {
                cart: { result: cart },
                products: { result: products },
                storeSelectorStateManager: { result: storeSelectorStateManager },
                orgUnitLocations: { result: orgUnitLocations }
            },
            resources
        } = this.props;

        return (
            cart?.cart?.CartLines &&
            cart.cart.CartLines.length > 0 && (
                <DeliveryMethods
                    context={this.props.context}
                    cartState={cart}
                    products={products}
                    shipItText={resources.shipToAddressDisplayText}
                    pickUpInStoreText={resources.pickItUpDisplayText}
                    changeStoreText={resources.changeStoreDisplayText}
                    storeSelectorStateManager={storeSelectorStateManager}
                    orgUnitLocations={orgUnitLocations}
                    showSpinner={this.setShowSpinner}
                    data={this.props.data}
                    config={this.props.config}
                />
            )
        );
    };

    private _getOrderSummaryErrors(cartLines: ICartlinesViewProps[] | null): IOrderSummaryErrors | undefined {
        const allErrors =
            (cartLines &&
                cartLines
                    .map((cartline) => {
                        if (cartline.error) {
                            const fullError =
                                cartline.data && cartline.data.product.Name
                                    ? `${cartline.data.product.Name}: ${cartline.error}`
                                    : cartline.error;

                            return <div className='msc-alert__line'>{fullError}</div>;
                        }

                        return null;
                    })
                    .filter((line): line is JSX.Element => !!line)) ||
            [];

        return {
            Wrapper: {
                tag: Alert,
                className: 'msc-alert',
                isOpen: allErrors.length > 0,
                color: 'danger',
                assertive: true,
                'aria-label': this.props.resources.outOfStockErrorMessageHeader
            },
            header: (
                <div className='msc-alert__header' aria-hidden='true'>
                    <span className='msi-exclamation-triangle' />
                    <span>{this.props.resources.outOfStockErrorMessageHeader}</span>
                </div>
            ),
            errors: allErrors
        };
    }

    private _getOrderSummaryLineItems(): IOrderSummaryLines | undefined {
        const {
            data: {
                cart: { result: cart }
            },
            telemetry,
            resources
        } = this.props;

        const channelConfiguration = this.props.context && this.props.context.request && this.props.context.request.channel;

        return cart && cart.cart && channelConfiguration && cart.cart.CartLines && cart.cart.CartLines.length > 0
            ? OrderSummary({
                cart: cart.cart,
                subTotalLabel: resources.itemsWithCountLabel,
                shippingLabel: resources.estimatedShippingLabel,
                taxLabel: resources.estimatedTaxLabel,
                orderTotalLabel: resources.orderTotal,
                totalDiscountsLabel: resources.totalDiscountsLabel,
                context: this.props.context,
                typeName: this.props.typeName,
                id: this.props.id,
                toBeCalculatedText: resources.yetToBeCalculatedText,
                freeText: resources.priceFree,
                channelConfiguration: channelConfiguration,
                telemetry: telemetry,
                isOnCartPage: true
            })
            : undefined;
    }

    private _getOrderSummaryAccountDetails(): React.ReactNode {
        const { user } = this.props.context.request;

        return (
            user.isAuthenticated && (
                <>
                    <div className='msc-order-summary__account'>
                        <div>Your Farmlands Account</div>
                        <div>
                            {this.accountNumber} - {this.displayName}
                        </div>
                        {!this.state.isSingleAccount && (
                            <a className='msc-order-summary__switch-account' onClick={() => this._openHeaderDropdown()}>
                                Click here to switch Accounts
                            </a>
                        )}
                    </div>
                </>
            )
        );
    }

    private _openHeaderDropdown(): void {
        const button = document.getElementById('ms-header__account-button');
        return button?.click();
    }

    private _getPromoCode(): React.ReactNode {
        const {
            data: {
                cart: { result: cart }
            },
            resources
        } = this.props;

        return (
            cart && (
                <PromoCodeComponent
                    cart={cart}
                    promoCodeHeadingText={resources.promoCodeHeadingText}
                    promoPlaceholderText={resources.promoPlaceholderText}
                    promoCodeApplyButtonText={resources.promoCodeApplyButtonText}
                    appliedPromoCodeHeadingText={resources.appliedPromoCodeHeadingText}
                    collapseTimeOut={350}
                    removePromoAriaLabelFormat={resources.removePromoAriaLabelFormat}
                    removePromoText={resources.removePromoText}
                    invalidPromoCodeErrorText={resources.invalidPromoCodeErrorText}
                    failedToAddPromoCodeErrorText={resources.failedToAddPromoCodeErrorText}
                    failedToRemovePromoCodeErrorText={resources.failedToRemovePromoCodeErrorText}
                    duplicatePromoCodeErrorText={resources.duplicatePromotionErrorText}
                    context={this.props.context}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{}}
                />
            )
        );
    }

    private _getCartLines(): ICartlinesViewProps[] | null {
        const {
            data: {
                cart: { result: cart },
                wishlists: { result: wishlists }
            },
            context: {
                request: { channel: { PickupDeliveryModeCode } = { PickupDeliveryModeCode: undefined } }
            },
            config: config,
            telemetry,
            resources
        } = this.props;

        const imageSettings = this._getImageSettings(config.imageSettings);

        return cart && cart.cart.CartLines && cart.cart.CartLines.length > 0
            ? CartLineItems({
                cartlines: cart.cart.CartLines,
                products: this.props.data.products.result,
                deliveryOptions: this.state.deliveryOptions,
                productAvailabilites: this.props.data.productAvailabilites.result,
                productDeliveryOptions: this.props.data.deliveryOptions.result,
                imageSettings: imageSettings,
                isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
                maxCartlineQuantity: this.props.context.app.config.maxQuantityForCartLineItem,
                gridSettings: this.props.context.request.gridSettings!,
                context: this.props.context,
                typeName: this.props.typeName,
                id: this.props.id,
                resources: {
                    sizeString: resources.productDimensionTypeSize,
                    colorString: resources.productDimensionTypeColor,
                    configString: resources.productDimensionTypeConfiguration,
                    styleString: resources.productDimensionTypeStyle,
                    quantityDisplayString: resources.quantityDisplayText,
                    inputQuantityAriaLabel: resources.inputQuantityAriaLabel,
                    decrementButtonAriaLabel: resources.decrementButtonAriaLabel,
                    incrementButtonAriaLabel: resources.incrementButtonAriaLabel,
                    discountStringText: resources.discountStringText,
                    originalPriceText: resources.originalPriceText,
                    currentPriceText: resources.currentPriceText,
                    shippingChargesText: resources.shippingChargesText
                },
                telemetry: telemetry,
                addToWishlistButtonText: resources.addToWishlistButtonText,
                removeFromWishlistButtonText: resources.removeFromWishlistButtonText,
                removeButtonText: resources.removeCartButtonText,
                shipItText: resources.shipInsteadDisplayText,
                pickitUpText: resources.pickItUpDisplayText,
                changeStoreText: resources.changeStoreDisplayText,
                storeSelectorStateManager: this.props.data.storeSelectorStateManager.result,
                outOfStockText: resources.outOfStockErrorMessage,
                outOfRangeOneText: resources.errorMessageOutOfRangeOneLeft,
                outOfRangeFormatText: resources.errorMessageOutOfRangeFormat,
                validationErrorRemoveItemText: resources.validationErrorRemoveItemText,
                cartState: this.props.data.cart.result,
                orgUnitLocations: this.props.data.orgUnitLocations.result,
                removeItemClickHandler: this._removeItemFromCartHandler,
                moveToWishlistSuccessHandler: this._moveToWishlistSuccessHandler,
                updateCartLinesQuantitySuccessHandler: this._updateCartlineQuantity,
                wishlists: wishlists,
                defaultWishlistName: resources.defaultWishlistName,
                pickupDeliveryModeCode: PickupDeliveryModeCode,
                includeErrors: config.showOutOfStockErrors,
                cartLineDeliveryMethodsText: {
                    deliveryOnly: config.cartLineDeliveryOnly,
                    clickCollectOnly: config.cartLineClickCollectOnly,
                    outOfStockCC: config.cartLineClickCollectOutOfStock,
                    outOfStockDel: config.cartLineDeliveryOutOfStock,
                    oosNoBackOrder: config.cartLineNoBackOrder
                }
            })
            : null;
    }

    private _getSignInURL(signInUrl: string): string {
        if (MsDyn365.isBrowser) {
            return `${signInUrl}${signInUrl.indexOf('?') === -1 ? '?' : '&'}ru=${window.location.origin}/cart`;
        }
        return '';
    }

    private _getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        return (
            imageSettings || {
                viewports: {
                    xs: { q: `w=80&h=94&m=6`, w: 0, h: 0 },
                    sm: { q: `w=148&h=174&m=6`, w: 0, h: 0 },
                    lg: { q: `w=148&h=174&m=6`, w: 0, h: 0 }
                },
                lazyload: true
            }
        );
    };

    private _removeItemFromCartHandler = (cartlineToRemove: CartLine) => {
        if (this.props.data.cart.result) {
            this.setShowSpinner(true);
            const input = {
                cartLineIds: [cartlineToRemove.LineId!.toString()]
            };
            this.props.data.cart.result
                .removeCartLines(input)
                .then(() => {
                    if(this.props.data.cart.result?.cart.CartLines?.length === 0) {
                        if (MsDyn365.isBrowser) {
                            window.location.reload();
                        }
                    }
                    else {
                        this.setShowSpinner(false);
                    }
                })
                .catch((error) => {
                    this.props.telemetry.warning(error);
                    this.props.telemetry.debug('Unable to Remove Cart Line');
                    this.setShowSpinner(false);
                });
        }
    };

    private _moveToWishlistSuccessHandler = (result: IWishlistActionSuccessResult, cartline?: CartLine) => {
        if (result.status === 'ADDED' && cartline) {
            this._removeItemFromCartHandler(cartline);
        }
    };

    private _updateCartlineQuantity = (cartlineToUpdate: CartLine, quantity: number) => {
        if (this.props.data.cart.result) {
            const input = {
                cartLineId: cartlineToUpdate.LineId!.toString(),
                newQuantity: quantity
            };
            this.props.data.cart.result.updateCartLineQuantity(input).catch((error) => {
                this.props.telemetry.warning(error);
                this.props.telemetry.debug('Unable to update Cart Line quantity');
            });
        }
    };

    private _checkBackorderErrors = (cart: any) => {
        const { lineItems } = inventoryStocks;
        const currentInventoryStocks = lineItems.filter(p1 => cart.some((p2: any) => p1['ProductId'] === p2['ProductId']))
        return !!currentInventoryStocks.some((item) => item['PhysicalAvailableInventoryLevelCode'] === 'OOS' && !item['IsBackOrderAllowed']);
    };
}

export default Cart;
